import { Button, Card, IconButton, LuIcon, Menu, Separator, stopPropagation, Text } from "@overmap-ai/blocks"
import {
	type Procedure,
	type ProcedureFieldValues,
	type ProcedureTypeFields,
	type Stored,
	toUuidArray,
	EMPTY_ARRAY,
} from "@overmap-ai/common"
import {
	selectAttachmentsOfProcedureFieldValues,
	selectFieldValuesOfProcedure,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProcedureTypeFieldsById,
	selectProcedureTypeFieldsOfProcedureType,
	selectProcedureStepsOfProcedureType,
	selectProcedureTypeById,
} from "@overmap-ai/core"
import type { FieldValues } from "@overmap-ai/forms"
import { useSyncedState } from "@overmap-ai/hooks"
import * as RadixCollapsible from "@radix-ui/react-collapsible"
import { useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback, useMemo, useState } from "react"

import { interactiveCardCva } from "../../cva"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveOrganizationId, selectActiveProjectId } from "../../store/selectors"
import { ProcedureTypeDescriptionDialog } from "../Dialogs/ProcedureTypeDescriptionDialog"
import { FieldsRevisionDropdownSelect } from "../DropdownSelects/FieldsRevisionDropdownSelect"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useFieldValuesChangeDebounced,
	useHydrateFieldValuesWithAttachments,
} from "../Form/hooks"
import { ProcedureTypeFieldsSubmitter } from "../Form/ProcedureTypeFieldsSubmitter"
import {
	getAttachmentPayloadsFromFilesAndPromises,
	getFieldFilesAndPromisesToCreate,
	getFieldValuesAttachmentsToDelete,
	getIsFieldValuesDraft,
} from "../Form/utils"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import { ProcedureIndexLabel } from "../IndexLabels/ProcedureIndexLabel"
import { ProcedureStepItem } from "./ProcedureStepItem"

interface ProcedureListItemProps {
	procedure: Stored<Procedure>
	readonly?: boolean
}

export const ProcedureListItem = memo<ProcedureListItemProps>((props) => {
	const { procedure, readonly = false } = props

	const procedureType = useAppSelector(selectProcedureTypeById(procedure.procedure_type))

	if (!procedureType) {
		throw new Error(`Expected procedureType with uuid ${procedure.procedure_type} to exist in the store.`)
	}

	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedure.procedure_type))
	const latestProcedureTypeFields = useAppSelector(
		selectLatestProcedureTypeFieldsOfProcedureType(procedure.procedure_type),
	)
	const procedureFieldValues = useShallowEqaulSelector(selectFieldValuesOfProcedure(procedure.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const navigate = useNavigate()
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)
	const activeProjectId = useAppSelector(selectActiveProjectId)
	const [open, setOpen] = useState(false)

	const latestProcedureFieldValues = useMemo(() => {
		let ret: Stored<ProcedureFieldValues> | undefined = undefined

		for (const fieldValues of procedureFieldValues) {
			if (!ret || fieldValues.submitted_at > ret.submitted_at) {
				ret = fieldValues
			}
		}

		return ret
	}, [procedureFieldValues])

	const latestProcedureFieldValuesAttachments = useShallowEqaulSelector(
		selectAttachmentsOfProcedureFieldValues(latestProcedureFieldValues?.uuid ?? ""),
	)

	const procedureTypeFields = useShallowEqaulSelector(
		selectProcedureTypeFieldsOfProcedureType(procedure.procedure_type),
	)
	const currentProcedureTypeFields = useAppSelector(
		selectProcedureTypeFieldsById(latestProcedureFieldValues?.fields_revision ?? ""),
	)
	const [computedProcedureTypeFields, setComputedProcedureTypeFields] = useSyncedState(
		currentProcedureTypeFields ?? latestProcedureTypeFields,
	)

	const fieldSchema = useDeserializeFieldSchema(computedProcedureTypeFields?.fields ?? EMPTY_ARRAY)
	const isProcedureFieldValuesDraft = getIsFieldValuesDraft(latestProcedureFieldValues)

	const handleProcedureFieldValuesChange = useFieldValuesChangeDebounced(
		(allValues: FieldValues) => {
			if (!computedProcedureTypeFields?.uuid) return

			if (!isProcedureFieldValuesDraft || !latestProcedureFieldValues?.uuid) {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.procedureFieldValues.add({
					fields_revision: computedProcedureTypeFields.uuid,
					procedure: procedure.uuid,
					values: fieldSchema.serializeValues(values),
					published_at: null,
				})

				if (Object.keys(filesAndPromises).length !== 0) {
					void sdk.procedureFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromises,
							latestProcedureFieldValuesAttachments,
						),
					)
				}
			} else {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.procedureFieldValues.update({
					uuid: latestProcedureFieldValues.uuid,
					fields_revision: computedProcedureTypeFields.uuid,
					values: fieldSchema.serializeValues(values),
				})

				const attachmentsToDelete = getFieldValuesAttachmentsToDelete(
					latestProcedureFieldValuesAttachments,
					filesAndPromises,
				)

				const filesAndPromisesToCreate = getFieldFilesAndPromisesToCreate(
					latestProcedureFieldValuesAttachments,
					filesAndPromises,
				)

				if (attachmentsToDelete.length !== 0) {
					void sdk.procedureFieldValuesAttachments.bulkDelete(toUuidArray(attachmentsToDelete))
				}

				if (Object.keys(filesAndPromisesToCreate).length !== 0) {
					void sdk.procedureFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromisesToCreate,
							latestProcedureFieldValuesAttachments,
						),
					)
				}
			}
		},
		[
			fieldSchema,
			isProcedureFieldValuesDraft,
			latestProcedureFieldValues?.uuid,
			latestProcedureFieldValuesAttachments,
			computedProcedureTypeFields?.uuid,
			procedure.uuid,
		],
	)

	const handleProcedureFieldValuesSubmit = useCallback(
		(_values: FieldValues) => {
			if (!latestProcedureFieldValues?.uuid) return

			handleProcedureFieldValuesChange.flush()

			const [_offlineProcedureFieldValues, _promise] = sdk.procedureFieldValues.update({
				uuid: latestProcedureFieldValues.uuid,
				published_at: new Date().toISOString(),
			})
		},
		[latestProcedureFieldValues?.uuid, handleProcedureFieldValuesChange],
	)

	const handleDeleteProcedureFieldValues = useCallback(() => {
		if (!latestProcedureFieldValues?.uuid || !isProcedureFieldValuesDraft) return

		openConfirmDeleteDialog({
			title: "Delete draft",
			description: "Are you sure you want to delete this draft?",
			onAction: () => {
				handleProcedureFieldValuesChange.flush()
				void sdk.procedureFieldValues.delete(latestProcedureFieldValues.uuid)
			},
		})
	}, [
		isProcedureFieldValuesDraft,
		latestProcedureFieldValues?.uuid,
		openConfirmDeleteDialog,
		handleProcedureFieldValuesChange,
	])

	const handleDeleteProcedure = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete procedure",
			description: "Are you sure you want to delete this procedure? This action cannot be undone.",
			onAction: () => {
				void sdk.procedures.delete(procedure.uuid)
			},
		})
	}, [openConfirmDeleteDialog, procedure.uuid])

	const handleViewDetails = useCallback(() => {
		if (!activeOrganizationId || !activeProjectId) return

		void navigate({
			to: "/$organizationId/$projectId/procedures/$procedureId",
			params: {
				organizationId: activeOrganizationId,
				projectId: activeProjectId,
				procedureId: procedure.uuid,
			},
		})
	}, [activeOrganizationId, activeProjectId, navigate, procedure.uuid])

	const handleProcedureTypeFieldsSelect = useCallback(
		(fields: Stored<ProcedureTypeFields>) => {
			handleProcedureFieldValuesChange.flush()
			setComputedProcedureTypeFields(fields)
		},
		[handleProcedureFieldValuesChange, setComputedProcedureTypeFields],
	)

	const fieldValues = useDeserializeFieldValues(fieldSchema, latestProcedureFieldValues?.values)

	const fieldValuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		latestProcedureFieldValuesAttachments,
		sdk.procedureFieldValuesAttachments,
	)

	return (
		<RadixCollapsible.Root open={open} onOpenChange={setOpen} asChild>
			<Card role="listitem" className={cx("flex flex-col gap-4", interactiveCardCva())} size="sm">
				<RadixCollapsible.Trigger asChild>
					<div className="flex cursor-pointer flex-col gap-2">
						<div className="flex items-center justify-between gap-2">
							<div className="flex min-w-0 items-center gap-2">
								<ProcedureTypeIcon procedureTypeId={procedure.procedure_type} />
								<ProcedureIndexLabel procedureId={procedure.uuid} size="sm" />
								<Text size="sm" accentColor="base" className="truncate">
									{procedureType.name}
								</Text>
								<ProcedureTypeDescriptionDialog procedureType={procedureType} size="sm">
									<IconButton onClick={stopPropagation} variant="ghost" size="sm" accentColor="base">
										<LuIcon icon={modelIconMapping.info} />
									</IconButton>
								</ProcedureTypeDescriptionDialog>
							</div>

							<div className="flex items-center gap-2">
								<Menu.Root>
									<Menu.ClickTrigger>
										<IconButton
											onClick={stopPropagation}
											variant="ghost"
											size="sm"
											accentColor="base"
										>
											<LuIcon icon={modelIconMapping.more} />
										</IconButton>
									</Menu.ClickTrigger>
									<Menu.Content size="sm">
										<Menu.Item onSelect={handleViewDetails}>
											<LuIcon icon={modelIconMapping.externalLink} />
											View
										</Menu.Item>
										{!readonly && (
											<Menu.Item onSelect={handleDeleteProcedure}>
												<LuIcon icon={modelIconMapping.delete} />
												Delete
											</Menu.Item>
										)}
									</Menu.Content>
								</Menu.Root>
							</div>
						</div>
					</div>
				</RadixCollapsible.Trigger>

				<RadixCollapsible.Content className="flex flex-col gap-4">
					{!!computedProcedureTypeFields && (
						<>
							<Separator size="full" />

							<div className="flex flex-col gap-4">
								<FieldsRevisionDropdownSelect
									required
									disabled={isProcedureFieldValuesDraft}
									fieldsRevisions={procedureTypeFields}
									current={currentProcedureTypeFields}
									value={computedProcedureTypeFields}
									onValueChange={handleProcedureTypeFieldsSelect}
									size="sm"
								>
									<Button
										size="xs"
										accentColor="base"
										variant="surface"
										className="w-max"
										disabled={isProcedureFieldValuesDraft}
									>
										Revision {computedProcedureTypeFields.revision}
										<LuIcon icon={modelIconMapping.chevronDown} />
									</Button>
								</FieldsRevisionDropdownSelect>

								<ProcedureTypeFieldsSubmitter
									size="sm"
									procedureTypeFields={computedProcedureTypeFields}
									onSubmit={handleProcedureFieldValuesSubmit}
									values={fieldValuesWithAttachments}
									onValuesChange={handleProcedureFieldValuesChange}
									hideCancel={!isProcedureFieldValuesDraft}
									cancelText="Draft"
									cancelIcon={modelIconMapping.delete}
									onCancel={handleDeleteProcedureFieldValues}
									disabled={readonly}
									hideSubmit={!isProcedureFieldValuesDraft}
								/>
							</div>
						</>
					)}

					{procedureSteps.length > 0 && (
						<>
							<Separator size="full" />
							<div className="flex flex-col gap-2">
								{procedureSteps.map((procedureStep, index) => (
									<div key={procedureStep.uuid} className="flex flex-col gap-4">
										<ProcedureStepItem
											procedure={procedure}
											procedureStep={procedureStep}
											readonly={readonly}
										/>
										{index !== procedureSteps.length - 1 && <Separator size="full" />}
									</div>
								))}
							</div>
						</>
					)}
				</RadixCollapsible.Content>
			</Card>
		</RadixCollapsible.Root>
	)
})
ProcedureListItem.displayName = "ProcedureListItem"
